import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CoaDTO } from '@/shared/api/model';
import {
    useCreateCoa,
    useDeleteCoa,
    useGetAllCoas,
    useUpdateCoa,
} from "@/shared/api/client/coa-resource";
import { useEffect } from 'react';

// Types
export type ActionMode = 'view' | 'add' | 'edit' | 'delete';

export interface CoaNode {
    id: string;
    glNo: string;
    acName: string;
    acType: string;
    product: string;
    fxCrncy: string;
    fsKey: string;
    parent: string;
    level: number;
    canDelete: boolean;
    canAddChild: boolean;
    noOfAccounts: number;
    child: number;
    children?: CoaNode[];
}

interface CoaState {
    // Data
    rawData: CoaDTO[];
    treeData: CoaNode[];
    activeNode: CoaNode | null;

    // UI State
    actionMode: ActionMode;
    isModalOpen: boolean;
    expandedKeys: Set<string>;
    isLoading: boolean;
    error: Error | null;

    // Actions
    setActiveNode: (node: CoaNode | null) => void;
    setActionMode: (mode: ActionMode) => void;
    setModalOpen: (isOpen: boolean) => void;
    toggleExpanded: (key: string) => void;
    setExpandedKeys: (keys: Set<string>) => void;
    setRawData: (data: CoaDTO[]) => void;
    resetState: () => void;
}

// Utilities
const convertDtoToNode = (dto: CoaDTO): CoaNode => ({
    id: dto.glNo,
    glNo: dto.glNo,
    acName: dto.acName || '',
    acType: dto.acType || '',
    product: dto.product || '',
    fxCrncy: dto.fxCrncy || '',
    fsKey: dto.fsKey || '',
    parent: dto.parent || '0000',
    level: dto.lavel || 0,
    canDelete: dto.canDelete || false,
    canAddChild: dto.canAddChild || false,
    noOfAccounts: dto.noOfAccounts || 0,
    child: dto.child || 0,
});

const buildTree = (items: CoaDTO[]): CoaNode[] => {
    const nodes = items?.map(convertDtoToNode);
    const nodeMap = new Map<string, CoaNode>();

    // Initialize all nodes with empty children arrays
    nodes.forEach(node => {
        nodeMap.set(node.fsKey, { ...node, children: [] });
    });

    const roots: CoaNode[] = [];

    // Build the tree structure
    nodes.forEach(node => {
        const currentNode = nodeMap.get(node.fsKey)!;

        if (node.parent === '0000') {
            roots.push(currentNode);
        } else {
            const parentNode = nodeMap.get(node.parent);
            if (parentNode) {
                if (!parentNode.children) {
                    parentNode.children = [];
                }
                parentNode.children.push(currentNode);
            } else {
                // If parent not found, add as root
                roots.push(currentNode);
            }
        }
    });

    return roots;
};

// Initial state
const initialState = {
    rawData: [],
    treeData: [],
    activeNode: null,
    actionMode: 'view' as ActionMode,
    isModalOpen: false,
    expandedKeys: new Set<string>(),
    isLoading: false,
    error: null,
};

// Create store
export const useCoaStore = create<CoaState>()(
    devtools(
        (set, get) => ({
            ...initialState,

            setActiveNode: (node) => set({ activeNode: node }),

            setActionMode: (mode) => set({ actionMode: mode }),

            setModalOpen: (isOpen) => set({ isModalOpen: isOpen }),

            toggleExpanded: (key) => set(state => {
                const newKeys = new Set(state.expandedKeys);
                if (newKeys.has(key)) {
                    newKeys.delete(key);
                } else {
                    newKeys.add(key);
                }
                return { expandedKeys: newKeys };
            }),

            setExpandedKeys: (keys) => set({ expandedKeys: keys }),

            setRawData: (data) => set(state => ({
                rawData: data,
                treeData: buildTree(data)
            })),

            resetState: () => set(initialState),
        }),
        { name: 'CoaStore' }
    )
);

// Hook for using the store with API integration
